import type z from 'zod';
import { skipToken, useQuery } from '@tanstack/react-query';
import type { ApiResponse } from '@/types/api-types';
import type { Nullish } from '@/types/nullish';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/cdr/api-schema';
import { cdrReportSchemas } from '@/components/patient-card/patient-card-schema';
import { cdrApi } from '@/services';

const API_PATH = '/v1/report/queryPathologyReportContent/{id}' as const;

type APIResponse = ApiResponse<paths, typeof API_PATH>;

type PathologyReportContentData = z.infer<typeof cdrReportSchemas.pathology>;

const queryFn = async (id: string, signal: AbortSignal) => {
  const { data: response } = await cdrApi.get<APIResponse>(
    API_PATH.replace('{id}', id),
    { signal },
  );

  const result = cdrReportSchemas.pathology.safeParse(response.data);

  if (result.error) {
    throw new Error(result.error.message);
  }

  return result.data;
};

export const useQueryPathologyReportContent = (
  id: string | Nullish,
  options?: QueryOptions<PathologyReportContentData>,
) => {
  const hasId = !!id;
  const effectiveId = id ?? '';

  return useQuery({
    queryKey: [API_PATH, id],
    queryFn: hasId ? ({ signal }) => queryFn(effectiveId, signal) : skipToken,
    enabled: hasId,
    ...options,
  });
};
